import DOMPurify from 'dompurify';
import { formatPhoneNumber } from 'lib/numUtils';
import { store } from 'redux/lib/configureStore.js';
import { formatDate } from 'lib/dateUtilities';
import normalizeMessageType from './normalizeMessageType';
import selectors from 'selectors/selectors.js';
import formatDatetimeTheUniverseLifeAndEverything from 'lib/formatDatetimeTheUniverseLifeAndEverything.js';

const arrayCommaJoin = (arr) => {
  if (arr.length === 1) return arr[0];
  const firsts = arr.slice(0, arr.length - 1);
  const last = arr[arr.length - 1];
  return firsts.join(', ') + ' and ' + last;
};

export const templateParser = (template = '', contact = {}, pipelineLeads = null, message = {}) => {
  const threadMeta = message.thread_meta || {};
  const userProfile = (store.getState().user || {}).profile || {};
  const serviceProvider = store.getState().currentServiceProvider || {};
  const serviceProviderLocations = serviceProvider.locations || [];
  const servicePersons = selectors.serviceProviderServicePersons(store.getState());
  const activeLead = selectors.activeLead(store.getState()) || {};
  let activePipeline = (pipelineLeads || []).find((n) => String(n.status_id) === '1') || {};
  if(Object.keys(activePipeline).length === 0 && (pipelineLeads || []).length > 0) {
    activePipeline = pipelineLeads[0]
  }
  const activePipelineContact = (activeLead.contacts || []).find(n => n.id === activePipeline.contact_id) || {};
  const todos = (activeLead || {}).todos || [];
  const appointment = todos.find((n) => n.todo_type === '9' && n.pipeline_lead_id === activePipeline.id);
  const appointmentDate = (appointment || {}).target_datetime;
  const servicePersonId = activePipeline.service_person_id;
  const servicePerson = servicePersons.find((n) => n.id === servicePersonId);
  let servicePersonName = servicePerson
    ? [servicePerson.namePrefix, servicePerson.firstName, servicePerson.lastName]
        .filter((n) => !!n)
        .join(' ')
        .trim()
    : '';
  servicePersonName = `${servicePersonName}${(servicePerson || {}).nameSuffix ? `, ${servicePerson.nameSuffix}` : ''}`;

  let replaceSet = {
    first_name: contact.first_name || threadMeta.first_name,
    last_name: contact.last_name || threadMeta.last_name,
    full_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `${threadMeta.first_name || ''} ${threadMeta.last_name || ''}`.trim(),
    user_first_name: `${userProfile.first_name}`,
    user_last_name: `${userProfile.last_name}`,
    user_full_name: `${userProfile.first_name} ${userProfile.last_name}`,
    contact_first_name: activePipelineContact.first_name || contact.first_name,
    contact_last_name: activePipelineContact.last_name || contact.last_name,
    contact_full_name: activePipelineContact.full_name || contact.full_name,
    service_name: '',
    service_value: '',
    service_provider_name: `${serviceProvider.name}`,
    service_location_name: '',
    service_location_address: '',
    service_person_name: servicePersonName,
    appointment_datetime: '',
  };

  if (appointmentDate) {
    replaceSet.appointment_datetime = formatDatetimeTheUniverseLifeAndEverything(appointmentDate);
  }

  //calculate service name and value
  if (pipelineLeads && template.includes('{{service_')) {
    let filteredPipelineLeads = (pipelineLeads || []).filter((n) => String(n.status_id) === '1');
    if(filteredPipelineLeads.length === 0 && pipelineLeads.length > 0) {
      filteredPipelineLeads = [pipelineLeads[0]]
    }
    let serviceNames = [];
    let serviceValues = [];
    let serviceLocation = null;
    for (let i = 0; i < filteredPipelineLeads.length; i++) {
      let serviceInterests = filteredPipelineLeads[i].service_interests || [];
      serviceLocation = serviceProviderLocations.find((el) => String(el.id) === String(filteredPipelineLeads[i].service_location_id));
      for (let j = 0; j < serviceInterests.length; j++) {
        serviceNames.push(serviceInterests[j].service_name);
        serviceValues.push(serviceInterests[j].service_total_value);
      }
    }

    if (serviceLocation) {
      const address = `${serviceLocation.address_line_1 || ''} ${serviceLocation.address_line_2 || ''}`.trim();
      const fullAddress = `${address}, ${serviceLocation.city || ''}, ${serviceLocation.state || ''} ${serviceLocation.zip_code || ''}`.trim();
      if(serviceLocation.alternate_business_name) {
        replaceSet.service_provider_name = serviceLocation.alternate_business_name
      }
      replaceSet.service_location_name = serviceLocation.service_location_name;
      if (fullAddress.length > 2) {
        replaceSet.service_location_address = fullAddress;
      }
    }
    if (serviceNames.length > 0) {
      replaceSet.service_name = arrayCommaJoin(serviceNames);
    }
    if (serviceValues.length > 0) {
      replaceSet.service_value = arrayCommaJoin(serviceValues);
    }
  }

  let replaceSetKeys = Object.keys(replaceSet);

  for (let i = 0; i < replaceSetKeys.length; i++) {
    let key = replaceSetKeys[i];
    let regex = new RegExp(`\\{\\{${key}\\}\\}`, 'gi');
    template = template ? template.replace(regex, replaceSet[key]) : template;
  }

  return template;
};

const structurePhoneCall = (lead) => {
  let structure = {};
  const { caller_number, caller_city, caller_state, event_status, creationDatetime, call_duration_seconds, first_name, last_name, id, type, service_location_id, business_name, caller_name } = lead;
  structure = {
    'First Name:': first_name,
    'Last Name:': last_name,
    'Phone Number:': formatPhoneNumber(caller_number),
    'Date:': formatDate(creationDatetime),
    'Call Duration:': call_duration_seconds + ' seconds',
    'Call Status:': event_status,
    'City:': caller_city,
    'State:': caller_state,
    event_type: type,
    event_id: id,
    service_location_id,
  };
  if (business_name) {
    structure['Business Name:'] = business_name;
  }
  if (lead['First Name:']) {
    structure['First Name:'] = lead['First Name:'];
  }
  if (lead['Last Name:']) {
    structure['Last Name:'] = lead['Last Name:'];
  }
  if (!first_name && !last_name) {
    if (caller_name) {
      let name_arr = caller_name ? caller_name.split(' ') : ['', ''];
      let first_name = name_arr[0];
      let last_name = name_arr.slice(1, name_arr.length).join(' ');
      structure['First Name:'] = first_name;
      structure['Last Name:'] = last_name;
    }
  }
  return structure;
};

export const sanitizeMessage = (msg) => {
  if (msg === undefined) {
    return '';
  }
  if (msg === null) {
    return '';
  }
  if (typeof msg === 'number') {
    return msg;
  }
  let string = '';
  string = String(msg).replace('<br>', '\n').replace('<br/>', '\n').replace('<br />', '\n');
  const clean = DOMPurify.sanitize(string);
  return clean || 'Unable to sanitize message';
};

const structureWebForm = (lead, data) => {
  let structure = {};
  const { phone_number, email_address, first_name, last_name, creationDatetime, id, type, message, service_location_id, business_name } = lead;
  structure = {
    'First Name:': first_name,
    'Last Name:': last_name,
    'Business Name:': business_name,
    'Phone Number:': formatPhoneNumber(phone_number),
    'Date:': formatDate(creationDatetime),
    'Email:': email_address,
    event_type: type,
    event_id: id,
    'Message:': sanitizeMessage(message),
    service_location_id,
  };
  let formFields;
  try {
    formFields = JSON.parse(lead.form_fields);
  } catch (e) {}
  if (formFields) {
    Object.entries(formFields).forEach(([key, value]) => {
      if (key !== 'reCAPTCHA Check' && value !== null) {
        if (String(value || '').length > 0) {
          if (key === 'How did you hear about us?') {
            structure[`${key}:`] = sanitizeMessage(formFields['How did you hear about us?']);
          } else {
            let cleanValue = Array.isArray(value) ? value.join(', ') : value;
            structure[`${key}:`] = sanitizeMessage(String(cleanValue));
          }
        }
      }
    });
  }
  return structure;
};

export const formatWebFormFields = (lead, data = true) => {
  const messageType = normalizeMessageType(lead.type);
  if (messageType === 'web_forms') {
    return structureWebForm(lead, data);
  }
  if (messageType === 'phone_calls') {
    return structurePhoneCall(lead, data);
  }
  if (messageType === 'texts') {
    return structureTextMessage(lead, data);
  }
};

const structureTextMessage = (lead) => {
  let structure = {};
  const { from_city, from_state, sms_message, message, creationDatetime, id, type, service_location_id, business_name } = lead;
  structure = {
    'Date:': formatDate(creationDatetime),
    'Message:': sms_message ? sanitizeMessage(sms_message) : sanitizeMessage(message),
    'City:': from_city,
    'State:': from_state,
    event_type: type,
    event_id: id,
    service_location_id,
  };
  if (business_name) {
    structure['Business Name:'] = business_name;
  }
  return structure;
};
